import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';
import {List} from 'immutable';

import {addToast} from '@albert-io/atomic';
import type {ClassroomModelV1, SubjectModelV2, StudentModelV2} from '@albert-io/models';
import errorCodes from 'client/errorCodes';
import {ERROR_TYPES} from 'client/constants';
import {genericMandarkRequest} from 'resources/mandark.resource';
import notifier from '@albert-io/notifier';
import {query} from '@albert-io/json-api-framework/request/builder';
import {replaceQueryParams} from 'client/history';

import {
  getClassroomInvitationsQuery as getInvitationsQuery,
  getClassroomsQuery,
  getIndiePracticeSubjectsQuery as getSubjectsQuery,
  getStudentQuery
} from './StudentDashboard.queries';

type State = {
  areClassroomsLoading: boolean;
  areInvitationsLoading: boolean;
  classrooms: List<ClassroomModelV1>;
  duplicateClassroom: ClassroomModelV1 | null;
  exceededHardCapLimit: boolean;
  isReady: boolean;
  isRequestPending: boolean;
  isSidebarExpanded: boolean;
  page: number;
  shouldRefetchResources: boolean;
  showJoinClassModal: boolean;
  student: StudentModelV2 | null;
  studentHasInvalidEmail: boolean;
  subjects: List<SubjectModelV2>;
  totalPages: number;
  // TODO: type these
  invitations: List<any>;
  openAssignments: List<any>;
  pastDueAssignments: List<any>;
};

type Payload = {
  isNewStudentDashboard: boolean;
  onReject: (err?: any) => void;
  onResolve: () => void;
  relationship: any;
};

type Actions = {
  bootstrapStudentDashboard: (pageOverride?: number, classroomId?: string) => void;
  clearDashboardCache: (page: number) => void;
  enrollStudent: (payload: Payload) => Promise<void>;
  setAreClassroomsLoading: (areClassroomsLoading: State['areClassroomsLoading']) => void;
  setAreInvitationsLoading: (areInvitationsLoading: State['areInvitationsLoading']) => void;
  setClassrooms: (classrooms: List<ClassroomModelV1>) => void;
  setDuplicateClassroom: (duplicateClassroom: State['duplicateClassroom']) => void;
  setExceededHardCapLimit: (hardCapExceeded: State['exceededHardCapLimit']) => void;
  setInvitations: (invitations: State['invitations']) => void;
  setIsSidebarExpanded: (isSidebarExpanded: State['isSidebarExpanded']) => void;
  setPage: (page: State['page']) => void;
  setShouldRefetchResources: (newValue: boolean) => void;
  setShowJoinClassModal: (showJoinClassModal: State['showJoinClassModal']) => void;
  setStudent: (student: StudentModelV2) => void;
  setStudentHasInvalidEmail: (hasInvalidEmail: State['studentHasInvalidEmail']) => void;
};

const initialState: State = {
  areClassroomsLoading: false,
  areInvitationsLoading: false,
  classrooms: List([]),
  duplicateClassroom: null,
  exceededHardCapLimit: false,
  invitations: List([]),
  isReady: false,
  isRequestPending: false,
  isSidebarExpanded: false,
  openAssignments: List([]),
  page: 1,
  pastDueAssignments: List([]),
  shouldRefetchResources: false,
  showJoinClassModal: false,
  student: null,
  studentHasInvalidEmail: false,
  subjects: List([]),
  totalPages: 1
};

const PAGE_SIZE = 6;

export const useStudentDashboard = create<State & Actions>()(
  devtools(
    immer((set, get) => ({
      ...initialState,
      bootstrapStudentDashboard: async (page, classroomId) => {
        const shouldEnrollStudentByParam = Boolean(classroomId);
        try {
          set((state) => {
            state.isReady = false;
          });
          const fetchedStudent = await getStudentQuery().getResourcePromise();
          const studentId = fetchedStudent.getId();
          const email = fetchedStudent.getEmail();

          const paginatedClassroomsQuery = getClassroomsQuery(studentId)
            .page(page)
            .pageSize(PAGE_SIZE);
          const fetchedClassrooms = await paginatedClassroomsQuery.getResourcePromise();

          const fetchedInvitations = await getInvitationsQuery(email).getResourcePromise();
          // @todo Refactor getClassroomInvitationsQuery with the query params that will achieve this same behavior
          const filteredInvitations = fetchedInvitations.filter(
            (invitation) =>
              !invitation.getClassroom().isEmpty() && !invitation.getIssuer().isEmpty()
          );

          const fetchedSubjects = await getSubjectsQuery().getResourcePromise();

          const assignmentInfo = fetchedStudent?.getMeta?.()?.getAssignmentInfo?.();
          const fetchedOpenAssignments = assignmentInfo?.get?.('open_assignments');
          const fetchedPastDueAssignments = assignmentInfo?.get?.('past_due_assignments');
          const totalPages = paginatedClassroomsQuery
            .getResourceMetadata()
            .getIn(['page', 'total_pages'], 1);

          set((state) => {
            state.openAssignments = fetchedOpenAssignments;
            state.pastDueAssignments = fetchedPastDueAssignments;
            state.classrooms = fetchedClassrooms;
            state.invitations = filteredInvitations;
            state.student = fetchedStudent;
            state.subjects = fetchedSubjects;
            state.totalPages = totalPages;
            state.isReady = shouldEnrollStudentByParam ? false : true;
          });
        } catch (_err) {
          addToast({
            title: 'Something went wrong',
            color: 'negative',
            message:
              'There was a problem loading your classrooms. If the problem persists, contact us.'
          });
          history.pushState(null, `/error?errorCode=${ERROR_TYPES.CRITICAL}`);
        }

        if (shouldEnrollStudentByParam) {
          const {enrollStudent, bootstrapStudentDashboard} = get();
          const relationship = {
            id: classroomId,
            type: 'classroom_v1'
          };
          await enrollStudent({
            isNewStudentDashboard: false,
            onReject: () => {
              set((state) => {
                state.isReady = true;
              });
              addToast({
                title: 'Something went wrong',
                color: 'negative',
                message:
                  'We were unable to complete your enrollment. Please verify the invitation with your teacher and try again.'
              });
            },
            onResolve: () => {
              replaceQueryParams({classroomId: null, classroomInvitationId: null});
              // replicates old behavior of reloading entire dashboard. // https://github.com/albert-io/nexus/issues/13443#issuecomment-3374784269
              bootstrapStudentDashboard(page);
            },
            relationship
          });
        }
      },
      clearDashboardCache: (page: number) => {
        const {student} = get();
        if (student) {
          const id = student.getId();
          const email = student.getEmail();
          getInvitationsQuery(email).invalidateInterest();
          getClassroomsQuery(id).page(page).pageSize(PAGE_SIZE).invalidateInterest();
          getSubjectsQuery().invalidateInterest();
          getStudentQuery().invalidateInterest();
        }
      },
      enrollStudent: async ({
        isNewStudentDashboard,
        onReject = () => {},
        onResolve = () => {},
        relationship = {}
      }) => {
        const {
          bootstrapStudentDashboard,
          clearDashboardCache,
          page,
          setExceededHardCapLimit,
          setShouldRefetchResources,
          setStudentHasInvalidEmail,
          student
        } = get();
        if (student) {
          try {
            const id = student.getId();

            await genericMandarkRequest(
              'post',
              query().mandarkEndpoint(['students_v2', id, 'relationships', 'classrooms_v1']).done(),
              {
                data: [relationship]
              }
            );

            if (isNewStudentDashboard) {
              setShouldRefetchResources(true);
            } else {
              clearDashboardCache(page);
            }

            addToast({
              duration: 10000,
              message: 'You have joined a new class',
              title: 'Success!',
              color: 'positive'
            });

            onResolve();

            set((state) => {
              state.isReady = true;
            });

            if (!isNewStudentDashboard) {
              // replicates old behavior of reloading entire dashboard // https://github.com/albert-io/nexus/issues/13443#issuecomment-3374784269
              bootstrapStudentDashboard(page);
            }
          } catch (err: any) {
            const exceededHardCapLimitError =
              err.response?.status === 403 &&
              err.response?.body.errors[0].code === errorCodes.FORBIDDEN.EXCEEDED_HARD_CAP_LIMIT;

            if (exceededHardCapLimitError) {
              setExceededHardCapLimit(true);
            }

            // don't notify for 400 errors (to suppress invalid join code)
            if (err.response?.status !== 400) {
              notifier.notify(err, {
                message: 'Failed to enroll student using relationship',
                tags: 'StudentDashboardProvider',
                context: {
                  relationship: relationship
                }
              });
            }

            // Catch the Errors.BadRequest.InvalidEmailDomain here to cover join code, email link, and invitation card enrollment flows
            const invalidEmail =
              err.response?.status === 400 &&
              err.response?.body.errors[0].code ===
                errorCodes.BAD_REQUEST.VALIDATION_ERROR.INVALID_EMAIL_DOMAIN;

            if (invalidEmail) {
              setStudentHasInvalidEmail(true);
            }

            onReject(err);
          }
        }
      },
      setAreClassroomsLoading: (areClassroomsLoading: State['areClassroomsLoading']) => {
        set((state) => {
          state.areClassroomsLoading = areClassroomsLoading;
        });
      },
      setAreInvitationsLoading: (areInvitationsLoading: State['areInvitationsLoading']) => {
        set((state) => {
          state.areInvitationsLoading = areInvitationsLoading;
        });
      },
      setClassrooms: (classrooms: State['classrooms']) => {
        set((state) => {
          state.classrooms = classrooms;
        });
      },
      setDuplicateClassroom: (duplicateClassroom: State['duplicateClassroom']) => {
        set((state) => {
          state.duplicateClassroom = duplicateClassroom;
        });
      },
      setExceededHardCapLimit: (hardCapExceeded: State['exceededHardCapLimit']) => {
        set((state) => {
          state.exceededHardCapLimit = hardCapExceeded;
        });
      },
      setInvitations: (invitations: State['invitations']) => {
        set((state) => {
          state.invitations = invitations;
        });
      },
      setIsSidebarExpanded: (isSidebarExpanded: State['isSidebarExpanded']) => {
        set((state) => {
          state.isSidebarExpanded = isSidebarExpanded;
        });
      },
      setPage: (page: State['page']) => {
        set((state) => {
          state.page = page;
        });
      },
      setShouldRefetchResources: (newValue: State['shouldRefetchResources']) => {
        set((state) => {
          state.shouldRefetchResources = newValue;
        });
      },
      setShowJoinClassModal: (showJoinClassModal: State['showJoinClassModal']) => {
        set((state) => {
          state.showJoinClassModal = showJoinClassModal;
        });
      },
      setStudent: (student: State['student']) => {
        set((state) => {
          state.student = student;
        });
      },
      setStudentHasInvalidEmail: (hasInvalidEmail: State['studentHasInvalidEmail']) => {
        set((state) => {
          state.studentHasInvalidEmail = hasInvalidEmail;
        });
      }
    }))
  )
);
